/**
 * DataTable and Pagination.
 *
 * Selection exists for one reason: "agrupar seleccionadas" (design screen 9).
 * Sorting is server-side — the contract exposes a fixed `sort` enum, so the
 * header buttons map onto it rather than reordering a page in the browser and
 * lying about the other pages.
 *
 * **On a phone the rows are cards (#111).** Below `PHONE` the same rows and columns render as a list
 * of labelled cards *instead of* the table — never beside it, or every `getBy*` in the suite would
 * find two of everything (design §2, rule 1). Each column says where it goes on a card with `card`,
 * and a card's field labels are the headers. The table branch is untouched. Sorting stays in the URL
 * on a phone: the cards carry no header to click.
 */

import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from './Icon.tsx'
import { EmptyState, ErrorState, LoadingRows } from './primitives.tsx'
import { useMediaQuery } from './useMediaQuery.ts'

/**
 * The phone tier, in the literal `responsive/lists.css` and the tier table in `tokens.css` use.
 * Custom properties cannot appear in `@media`, so the number is written out in both places.
 */
const PHONE = '(max-width: 600px)'

export interface Column<Row> {
  key: string
  header: ReactNode
  /** The field's label on a phone's card. Defaults to `header` — set it when that is not text. */
  label?: string
  /**
   * Where the column goes on a phone's card: the heading line (`title`), its right-hand end (`end`:
   * a status chip, the kebab), the row of buttons under the fields (`footer`), or nowhere
   * (`hidden`). Left out, the first column is the title, a column keyed `actions` is the footer, and
   * every other column is a labelled field. Anything status- or actions-like must say so.
   */
  card?: CardSlot
  /** Server sort key; presence turns the header into a sort control. */
  sortKey?: string
  align?: 'left' | 'right'
  /** Numeric/date content — gets tabular figures. */
  numeric?: boolean
  render: (row: Row) => ReactNode
  width?: string
}

export interface DataTableProps<Row> {
  rows: Row[] | undefined
  columns: Column<Row>[]
  rowKey: (row: Row) => string
  caption: string
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  empty?: ReactNode
  onRowClick?: (row: Row) => void
  /** Row-select for grouping. Omit to render a table with no checkbox column. */
  selection?: {
    selected: ReadonlySet<string>
    onToggle: (id: string) => void
    onToggleAll: (ids: string[]) => void
    selectable?: (row: Row) => boolean
  }
  sort?: {
    value: string
    onChange: (next: string) => void
  }
}

export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  caption,
  isLoading,
  error,
  onRetry,
  empty,
  onRowClick,
  selection,
  sort,
}: DataTableProps<Row>) {
  const { t } = useTranslation()
  // Here, before the early returns, so the hook order is the same on every render.
  const phone = useMediaQuery(PHONE)

  if (error) return <ErrorState error={error} onRetry={onRetry} />
  // One bar per column overflows a card: two stand for "a line of a card" instead.
  if (isLoading) return <LoadingRows columns={phone ? 2 : columns.length} />
  if (!rows || rows.length === 0) {
    return <>{empty ?? <EmptyState title={t('state.emptyTitle')} body={t('state.emptyBody')} />}</>
  }

  const selectableIds = selection
    ? rows.filter((row) => selection.selectable?.(row) ?? true).map(rowKey)
    : []
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selection?.selected.has(id))

  if (phone) {
    return (
      <DataCards
        rows={rows}
        columns={columns}
        rowKey={rowKey}
        caption={caption}
        onRowClick={onRowClick}
        selection={selection}
        selectableIds={selectableIds}
        allSelected={allSelected}
      />
    )
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {selection ? (
              <th scope="col" className="table__check">
                {/* The label fills the cell, so a tap beside the 20 px box still reaches it; the
                    touch pass sizes it to 44 px (#111's review). */}
                <label className="table__check-target">
                  <input
                    type="checkbox"
                    aria-label={t('common.selectAll')}
                    checked={allSelected}
                    onChange={() => selection.onToggleAll(selectableIds)}
                  />
                </label>
              </th>
            ) : null}
            {columns.map((column) => {
              const ascending = sort?.value === column.sortKey
              const descending = sort?.value === `-${column.sortKey}`
              const ariaSort = ascending ? 'ascending' : descending ? 'descending' : 'none'
              return (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  className={column.align === 'right' ? 'table__num' : undefined}
                  aria-sort={column.sortKey && sort ? ariaSort : undefined}
                >
                  {column.sortKey && sort ? (
                    <button
                      type="button"
                      className="table__sort"
                      aria-sort={ariaSort}
                      onClick={() =>
                        sort.onChange(descending ? column.sortKey! : `-${column.sortKey!}`)
                      }
                    >
                      {column.header}
                      <Icon name={descending ? 'chevronDown' : 'chevronRight'} size={12} />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = rowKey(row)
            const isSelected = selection?.selected.has(id) ?? false
            return (
              <tr
                key={id}
                data-selected={isSelected}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {selection ? (
                  <td className="table__check" onClick={(event) => event.stopPropagation()}>
                    <label className="table__check-target">
                      <input
                        type="checkbox"
                        aria-label={t('common.selectRow')}
                        checked={isSelected}
                        disabled={selection.selectable ? !selection.selectable(row) : false}
                        onChange={() => selection.onToggle(id)}
                      />
                    </label>
                  </td>
                ) : null}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={[
                      column.align === 'right' ? 'table__num' : '',
                      column.numeric ? 'tabular' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

type CardSlot = 'title' | 'end' | 'footer' | 'hidden'

/** Where a column goes on a card: its own `card`, or the defaults `Column.card` describes. */
function placeOf<Row>(column: Column<Row>, index: number): CardSlot | 'field' {
  if (column.card) return column.card
  if (index === 0) return 'title'
  return column.key === 'actions' ? 'footer' : 'field'
}

/** A cell with nothing in it is left off the card, rather than labelled over a blank. */
const isBlank = (value: ReactNode) => value === null || value === undefined || value === false || value === ''

/**
 * The phone's form of the table: one card per row, a heading line (the checkbox in a 44 px hit
 * area, the title, the end cells), the other columns as label/value pairs, and the footer's
 * buttons. `<dl>` because a card is exactly that — a set of labelled values — and select-all sits
 * above the list, where a header row would have held it.
 */
function DataCards<Row>({
  rows,
  columns,
  rowKey,
  caption,
  onRowClick,
  selection,
  selectableIds,
  allSelected,
}: Pick<DataTableProps<Row>, 'columns' | 'rowKey' | 'caption' | 'onRowClick' | 'selection'> & {
  rows: Row[]
  selectableIds: string[]
  allSelected: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="data-cards">
      {selection ? (
        <label className="checkbox data-cards__all">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => selection.onToggleAll(selectableIds)}
          />
          {t('common.selectAll')}
        </label>
      ) : null}
      {/* `role` spelled out: Safari drops a list's semantics once its bullets are styled away. */}
      <ul className="data-cards__list" role="list" aria-label={caption}>
        {rows.map((row) => {
          const id = rowKey(row)
          const isSelected = selection?.selected.has(id) ?? false
          const cells = columns
            .map((column, index) => ({ column, place: placeOf(column, index) }))
            .map((cell) => ({ ...cell, value: cell.column.render(row) }))
            .filter(({ value }) => !isBlank(value))
          const at = (place: CardSlot | 'field') => cells.filter((cell) => cell.place === place)
          const end = at('end')
          const fields = at('field')
          const footer = at('footer')

          return (
            <li
              key={id}
              className="data-card"
              data-selected={isSelected}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              <div className="data-card__head">
                {selection ? (
                  <label className="data-card__check" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={t('common.selectRow')}
                      checked={isSelected}
                      disabled={selection.selectable ? !selection.selectable(row) : false}
                      onChange={() => selection.onToggle(id)}
                    />
                  </label>
                ) : null}
                <div className="data-card__title">
                  {at('title').map(({ column, value }) => (
                    <div key={column.key}>{value}</div>
                  ))}
                </div>
                {end.length > 0 ? (
                  <div className="data-card__end">
                    {end.map(({ column, value }) => (
                      <div key={column.key}>{value}</div>
                    ))}
                  </div>
                ) : null}
              </div>
              {fields.length > 0 ? (
                <dl className="data-card__fields">
                  {fields.map(({ column, value }) => (
                    <div key={column.key}>
                      <dt>{column.label ?? column.header}</dt>
                      <dd className={column.numeric ? 'tabular' : undefined}>{value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {footer.length > 0 ? (
                <div className="data-card__footer">
                  {footer.map(({ column, value }) => (
                    <Fragment key={column.key}>{value}</Fragment>
                  ))}
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function Pagination({
  page,
  perPage,
  total,
  totalPages,
  onPage,
}: {
  page: number
  perPage: number
  total: number
  totalPages: number
  onPage: (next: number) => void
}) {
  const { t } = useTranslation()
  if (total === 0) return null

  const first = (page - 1) * perPage + 1
  const last = Math.min(page * perPage, total)
  const windowed = pageWindow(page, totalPages)

  return (
    <nav className="pagination" aria-label={t('common.page')}>
      <span className="tabular">
        {t('common.showing')} {first}–{last} {t('common.of')} {total}
      </span>
      <div className="pagination__pages">
        <button
          type="button"
          className="pagination__page pagination__step"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label={t('common.previous')}
        >
          <Icon name="chevronLeft" size={14} />
        </button>
        {/* The phone's pager, ‹ «Página 3 de 120» ›: CSS shows this and hides the numbered buttons
            and the gaps there, and does the opposite everywhere else (#111). */}
        <span className="pagination__status tabular">
          {t('common.page')} {page} {t('common.of')} {totalPages}
        </span>
        {windowed.map((candidate, index) =>
          candidate === null ? (
            <span key={`gap-${index}`} className="pagination__gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={candidate}
              type="button"
              className="pagination__page"
              aria-current={candidate === page ? 'page' : undefined}
              onClick={() => onPage(candidate)}
            >
              {candidate}
            </button>
          ),
        )}
        <button
          type="button"
          className="pagination__page pagination__step"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label={t('common.next')}
        >
          <Icon name="chevronRight" size={14} />
        </button>
      </div>
    </nav>
  )
}

/** First, last, and a window around the current page; `null` renders an ellipsis. */
function pageWindow(page: number, totalPages: number): (number | null)[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1)
  const pages = new Set<number>([1, totalPages, page, page - 1, page + 1])
  const sorted = [...pages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b)
  const out: (number | null)[] = []
  let previous = 0
  for (const current of sorted) {
    if (previous && current - previous > 1) out.push(null)
    out.push(current)
    previous = current
  }
  return out
}
